import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "../../App";
import { CompletionPhotoStrip } from "./CompletionPhotoStrip";
import { ContractorCompleteDialog } from "./ContractorCompleteDialog";
import { contractorPhotoUrl, uploadCompletionPhotos } from "./completionPhotoApi";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Lock, ZoomIn } from "lucide-react";
import { DefectImageLightbox } from "./DefectImageLightbox";

const priorityColor = (p) => {
  switch (p) {
    case "high":
      return "bg-red-100 text-red-700";
    case "medium":
      return "bg-amber-100 text-amber-700";
    case "low":
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

/**
 * Read-only defect details + a Complete / Incomplete status toggle.
 * Used on the internal Contractor Defects page and the public contractor link.
 */
export const ContractorDefectCard = ({ defect, onSetStatus, updating }) => {
  const { token } = useParams();
  const isComplete = defect.status === "completed";
  const isClosedOut = !!defect.closed_out;
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [photos, setPhotos] = useState(defect.completion_photos || []);
  const [completing, setCompleting] = useState(false);
  useEffect(() => setPhotos(defect.completion_photos || []), [defect.completion_photos]);

  const photoBase = `${API}/public/contractor-defects/${token}/${defect.id}/photos`;
  const changeStatus = (v) => (v === "completed" && token ? setCompleting(true) : onSetStatus(defect.id, v));
  const confirmComplete = async (files) => {
    if (files.length) setPhotos((await uploadCompletionPhotos(photoBase, files)).completion_photos || []);
    await onSetStatus(defect.id, "completed");
  };

  return (
    <Card className="dashboard-card" data-testid={`contractor-defect-card-${defect.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-semibold text-slate-900">
                {defect.number}
              </span>
              {defect.dwelling_number && (
                <span className="text-xs text-slate-500">
                  Dwelling {defect.dwelling_number}
                </span>
              )}
              <Badge className={`text-xs ${priorityColor(defect.priority)}`}>
                {defect.priority}
              </Badge>
              {isClosedOut && (
                <Badge className="text-xs bg-slate-800 text-white flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Closed Out
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <p className="text-xs text-slate-500 uppercase">Area</p>
                <p className="font-medium">{defect.area}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Element</p>
                <p className="font-medium">{defect.element}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Issue</p>
                <p className="font-medium">{defect.issue}</p>
              </div>
            </div>

            {defect.details && (
              <p className="text-sm text-slate-600">{defect.details}</p>
            )}

            {defect.images && defect.images.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {defect.images.map((img, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setLightboxIndex(i)}
                    className="relative group w-20 h-20 rounded border overflow-hidden cursor-zoom-in"
                    data-testid={`defect-photo-thumb-${defect.id}-${i}`}
                    aria-label={`Zoom defect photo ${i + 1}`}
                  >
                    <img
                      src={img}
                      alt={`Defect ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                      <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </span>
                  </button>
                ))}
              </div>
            )}
            {token && (
              <CompletionPhotoStrip photos={photos} testId={`contractor-completion-photos-${defect.id}`}
                urlFor={(p) => contractorPhotoUrl(token, defect.id, p.id)}
                onOpen={(p) => window.open(contractorPhotoUrl(token, defect.id, p.id, false), "_blank")}
                deleteUrlFor={(p) => `${photoBase}/${p.id}`}
                canDelete={(p) => !isClosedOut && p.uploaded_by === "contractor"}
                onChanged={(d) => setPhotos(d.completion_photos || [])} />
            )}
          </div>

          {/* Status dropdown */}
          <div className="flex sm:flex-col gap-2 sm:w-44 shrink-0">
            {isClosedOut ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Lock className="w-4 h-4" /> Locked
              </div>
            ) : (
              <Select
                value={isComplete ? "completed" : "in_progress"}
                onValueChange={changeStatus}
                disabled={updating}
              >
                <SelectTrigger
                  className={`w-full ${
                    isComplete
                      ? "border-green-400 text-green-700"
                      : "border-amber-400 text-amber-700"
                  }`}
                  data-testid={`contractor-status-select-${defect.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">Incomplete</SelectItem>
                  <SelectItem value="completed">Complete</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardContent>

      {defect.images && defect.images.length > 0 && (
        <DefectImageLightbox
          images={defect.images}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
      {token && (
        <ContractorCompleteDialog open={completing} onOpenChange={setCompleting}
          remaining={Math.max(0, 3 - photos.length)} onConfirm={confirmComplete} />
      )}
    </Card>
  );
};
